import React, {
  createContext,
  type CSSProperties,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Spinner } from "@/web/components/primitives/spinner";
import { getTextColor, isLighterThanOrEqualToThreshold, LIGHTEST_COLOR } from "@/web/lib/colors";
import { env } from "@/web/lib/env";
import { cn } from "@/web/lib/styling";

// ============================================================================
// Dropdown Lock Context
// ============================================================================

type DropdownLockContextValue = {
  isLocked: boolean;
  lock: () => void;
  unlock: () => void;
};

const DropdownLockContext = createContext<DropdownLockContextValue | null>(null);

export function DropdownLockProvider({ children }: { children: ReactNode }) {
  const [lockCount, setLockCount] = useState(0);
  const lock = useCallback(() => setLockCount((count) => count + 1), []);
  const unlock = useCallback(() => setLockCount((count) => Math.max(0, count - 1)), []);
  return (
    <DropdownLockContext.Provider value={{ isLocked: lockCount > 0, lock, unlock }}>
      {children}
    </DropdownLockContext.Provider>
  );
}

export function useDropdownLock() {
  const context = useContext(DropdownLockContext);
  return context ?? { isLocked: false, lock: () => {}, unlock: () => {} };
}

// ============================================================================
// BusinessCard context
// ============================================================================

type CampaignColors = {
  primary?: string;
  secondary?: string;
  tertiary?: string;
  text?: string;
};

type BusinessCardContextValue = {
  colors?: CampaignColors;
  fullBleedBackground: boolean;
};

const BusinessCardContext = createContext<BusinessCardContextValue | null>(null);

function buildCssColors(colors?: CampaignColors): React.CSSProperties {
  return {
    "--campaign-primary": resolveBrandColor(colors?.primary),
    ...(colors?.secondary
      ? {
          "--campaign-secondary": colors.secondary,
          "--campaign-secondary-text": getTextColor(colors.secondary),
        }
      : {}),
  } as React.CSSProperties;
}

function buildBackgroundImageStyle({
  bgImage,
  bgImageUrl,
}: Pick<BusinessCardProps, "bgImage" | "bgImageUrl">) {
  if (!bgImage && !bgImageUrl) return undefined;
  return {
    backgroundImage: bgImageUrl
      ? `url(${bgImageUrl})`
      : `url(${env.VITE_WITHCO_IMAGE_CDN_BASE}/${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  } satisfies React.CSSProperties;
}

function getSlot<
  T extends React.JSXElementConstructor<{ children: ReactNode; className?: string }>,
>(children: React.ReactNode[], component: T) {
  return children.find((child) => React.isValidElement(child) && child.type === component);
}

// ============================================================================
// BusinessCard root
// ============================================================================

export type BusinessCardProps = {
  children?: ReactNode;
  /** CDN path (will be prefixed with VITE_WITHCO_IMAGE_CDN_BASE) */
  bgImage?: string;
  /** Full URL for background image (used as-is, no CDN prefix) */
  bgImageUrl?: string;
  colors?: CampaignColors;
  className?: string;
  isLoading?: boolean;
  heroHeight?: number;
  fullBleedBackground?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onBodyMouseEnter?: () => void;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "onMouseEnter" | "onMouseLeave">;

const DEFAULT_HERO_HEIGHT = 355;

/** Fallback when a card has no brand `colorPrimary`. Matches TTW/share-card previews. */
const FALLBACK_BRAND_COLOR = "#3c524f";

function resolveBrandColor(colorHex: string | undefined): string {
  return (colorHex ?? FALLBACK_BRAND_COLOR).trim();
}

function isSixDigitHex(color: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(color);
}

function brandColorWithAlpha(color: string, alphaPercent: number): string {
  if (isSixDigitHex(color)) {
    const alphaHex = Math.round((alphaPercent / 100) * 255)
      .toString(16)
      .padStart(2, "0");
    return `${color}${alphaHex}`;
  }
  return `color-mix(in srgb, ${color} ${alphaPercent}%, transparent)`;
}

/** Top-down wash ending in the brand color — full-bleed browse cards. */
function fullBleedDownOverlayStyle(colorHex: string | undefined): CSSProperties {
  const color = resolveBrandColor(colorHex);
  return {
    backgroundImage: `linear-gradient(to bottom, rgb(0 0 0 / 0.16) 0%, rgb(0 0 0 / 0.08) 50%, ${brandColorWithAlpha(color, 84)} 100%)`,
  };
}

/** Bottom-up brand wash over the hero — full-bleed browse cards. */
function fullBleedUpOverlayStyle(colorHex: string | undefined): CSSProperties {
  const color = resolveBrandColor(colorHex);
  return {
    backgroundImage: `linear-gradient(to top, ${brandColorWithAlpha(color, 88)} 0%, transparent 45%, ${brandColorWithAlpha(color, 26)} 100%)`,
  };
}

const BusinessCardRoot = React.forwardRef<HTMLDivElement, BusinessCardProps>(function BusinessCard(
  {
    children,
    bgImage,
    bgImageUrl,
    colors,
    className,
    isLoading,
    heroHeight = DEFAULT_HERO_HEIGHT,
    fullBleedBackground = false,
    onMouseEnter,
    onMouseLeave,
    onBodyMouseEnter,
    ...props
  },
  ref,
) {
  const childArray = React.Children.toArray(children);
  const topBarSlot = getSlot(childArray, BusinessCardTopBar);
  const bodySlot = getSlot(childArray, BusinessCardBody);
  const attributionSlot = getSlot(childArray, BusinessCardAttribution);
  const progressSlot = getSlot(childArray, BusinessCardProgress);
  const metricsSlot = getSlot(childArray, BusinessCardMetrics);

  const colorStyle = buildCssColors(colors);
  const needsBorder =
    !colors?.primary || isLighterThanOrEqualToThreshold(colors.primary, LIGHTEST_COLOR);
  const backgroundImageStyle = buildBackgroundImageStyle({ bgImage, bgImageUrl });

  const wrapperClassName = cn(
    "flex h-[440px] flex-col overflow-hidden rounded-none md:max-w-[500px]",
    needsBorder && "border border-black/20",
    className,
  );

  const providerValue = useMemo(
    () => ({ colors, fullBleedBackground }),
    [colors, fullBleedBackground],
  );

  return (
    <BusinessCardContext.Provider value={providerValue}>
      <div style={colorStyle}>
        <BusinessCardShell
          ref={ref}
          wrapperClassName={wrapperClassName}
          heroHeight={heroHeight}
          fullBleedBackground={fullBleedBackground}
          backgroundImageStyle={backgroundImageStyle}
          isLoading={isLoading}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onBodyMouseEnter={onBodyMouseEnter}
          topBarSlot={topBarSlot}
          bodySlot={bodySlot}
          attributionSlot={attributionSlot}
          progressSlot={progressSlot}
          metricsSlot={metricsSlot}
          {...props}
        />
      </div>
    </BusinessCardContext.Provider>
  );
});

type BusinessCardShellProps = {
  wrapperClassName: string;
  heroHeight: number;
  fullBleedBackground: boolean;
  backgroundImageStyle: React.CSSProperties | undefined;
  isLoading?: boolean;
  onBodyMouseEnter?: () => void;
  topBarSlot?: React.ReactNode;
  bodySlot?: React.ReactNode;
  attributionSlot?: React.ReactNode;
  progressSlot?: React.ReactNode;
  metricsSlot?: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "className">;

const BusinessCardShell = React.forwardRef<HTMLDivElement, BusinessCardShellProps>(
  function BusinessCardShell(
    {
      wrapperClassName,
      heroHeight,
      fullBleedBackground,
      backgroundImageStyle,
      isLoading,
      onBodyMouseEnter,
      topBarSlot,
      bodySlot,
      attributionSlot,
      progressSlot,
      metricsSlot,
      ...props
    },
    ref,
  ) {
    const context = useContext(BusinessCardContext);
    const brandPrimary = context?.colors?.primary;
    const fullBleedSurfaceStyle: CSSProperties | undefined = fullBleedBackground
      ? { backgroundColor: resolveBrandColor(brandPrimary) }
      : undefined;

    return (
      <div ref={ref} className={wrapperClassName} {...props}>
        <div
          className={cn(
            "relative flex h-full flex-col overflow-hidden",
            fullBleedBackground ? "text-white" : "bg-[#FAFAF6]",
          )}
          style={fullBleedSurfaceStyle}
        >
          {fullBleedBackground ? (
            <>
              {backgroundImageStyle && (
                // oxlint-disable jsx-a11y/no-static-element-interactions hover interaction only
                // oxlint-disable jsx-a11y/click-events-have-key-events hover interaction only
                <div
                  className="absolute inset-0 scale-[1.02]"
                  onClick={onBodyMouseEnter}
                  onMouseEnter={onBodyMouseEnter}
                  style={backgroundImageStyle}
                />
                // oxlint-enable jsx-a11y/no-static-element-interactions hover interaction only
                // oxlint-enable jsx-a11y/click-events-have-key-events hover interaction only
              )}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={fullBleedDownOverlayStyle(brandPrimary)}
              />
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={fullBleedUpOverlayStyle(brandPrimary)}
              />
              <div className="relative z-10 flex min-h-0 flex-1 flex-col text-white">
                {isLoading ? (
                  <div className="flex flex-1 items-center justify-center">
                    <Spinner />
                  </div>
                ) : (
                  <>
                    <div className="flex min-h-0 flex-1 flex-col px-6 pt-6">
                      {topBarSlot}
                      {bodySlot}
                      {attributionSlot && <div className="mt-auto">{attributionSlot}</div>}
                    </div>
                    {progressSlot}
                    {metricsSlot}
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="relative shrink-0 overflow-hidden" style={{ height: heroHeight }}>
                {backgroundImageStyle && (
                  // oxlint-disable jsx-a11y/no-static-element-interactions hover interaction only
                  // oxlint-disable jsx-a11y/click-events-have-key-events hover interaction only
                  <div
                    className="absolute inset-0 scale-[1.02]"
                    onClick={onBodyMouseEnter}
                    onMouseEnter={onBodyMouseEnter}
                    style={backgroundImageStyle}
                  />
                  // oxlint-enable jsx-a11y/no-static-element-interactions hover interaction only
                  // oxlint-enable jsx-a11y/click-events-have-key-events hover interaction only
                )}
                <div className="absolute inset-0 bg-linear-to-b from-black/12 via-black/6 to-black/72" />
                <div className="absolute inset-0 bg-linear-to-t from-black/28 via-transparent to-black/10" />
                <div className="relative z-10 flex h-full flex-col px-6 pt-6 text-white">
                  {isLoading ? (
                    <div className="flex flex-1 items-center justify-center">
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      {topBarSlot}
                      {bodySlot}
                      {attributionSlot && <div className="mt-auto pb-6">{attributionSlot}</div>}
                    </>
                  )}
                </div>
              </div>
              {!isLoading && progressSlot}
              {!isLoading && metricsSlot}
            </>
          )}
        </div>
      </div>
    );
  },
);

// ============================================================================
// Slot primitives
// ============================================================================

export function BusinessCardTopBar({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("flex items-center justify-between gap-3", className)}>{children}</div>;
}

export function BusinessCardActions({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("flex items-center gap-2 self-center", className)}>{children}</div>;
}

export function BusinessCardBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("mt-4 flex min-h-0 flex-1 flex-col gap-0", className)}>{children}</div>;
}

export function BusinessCardTitle({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("withus-strong text-2xl", className)}>{children}</div>;
}

export function BusinessCardTags({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mt-3 flex flex-wrap items-center gap-1.5 pt-1", className)}>{children}</div>
  );
}

export function BusinessCardTag({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "max-w-full truncate rounded-none border-2 border-white bg-transparent px-[10px] py-[6px] text-[11px] font-semibold tracking-widest text-white uppercase",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function BusinessCardAttribution({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("text-sm text-white/90", className)}>{children}</div>;
}

export function BusinessCardProgress({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const context = useContext(BusinessCardContext);
  const isFullBleedBackground = context?.fullBleedBackground ?? false;
  return (
    <div
      className={cn(
        isFullBleedBackground
          ? "bg-transparent px-6 py-3"
          : "border-black/8 bg-[#FAFAF6] px-6 py-3",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function BusinessCardMetrics({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const context = useContext(BusinessCardContext);
  const isFullBleedBackground = context?.fullBleedBackground ?? false;
  return (
    <div
      className={cn(
        isFullBleedBackground
          ? "border-t border-white/10 px-6 pt-4 pb-5"
          : "bg-[#FAFAF6] px-6 py-4",
        className,
      )}
    >
      {children}
    </div>
  );
}

// ============================================================================
// useClickOutside
// ============================================================================

export function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  enabled: boolean,
  onClickOutside: () => void,
) {
  useEffect(() => {
    if (!enabled) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClickOutside();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [ref, enabled, onClickOutside]);
}

// ============================================================================
// Compound component
// ============================================================================

type BusinessCardCompoundComponent = typeof BusinessCardRoot & {
  TopBar: typeof BusinessCardTopBar;
  Actions: typeof BusinessCardActions;
  Body: typeof BusinessCardBody;
  Title: typeof BusinessCardTitle;
  Tags: typeof BusinessCardTags;
  Tag: typeof BusinessCardTag;
  Attribution: typeof BusinessCardAttribution;
  Progress: typeof BusinessCardProgress;
  Metrics: typeof BusinessCardMetrics;
};

export const BusinessCard = Object.assign(BusinessCardRoot, {
  TopBar: BusinessCardTopBar,
  Actions: BusinessCardActions,
  Body: BusinessCardBody,
  Title: BusinessCardTitle,
  Tags: BusinessCardTags,
  Tag: BusinessCardTag,
  Attribution: BusinessCardAttribution,
  Progress: BusinessCardProgress,
  Metrics: BusinessCardMetrics,
}) as BusinessCardCompoundComponent;
